import { useState, useEffect } from "react";
import { api } from "../../../lib/api";
import { fmtDT } from "../../../lib/constants";
import { toast } from "sonner";
import { XCircle, QrCode, ShieldCheck, Ruler } from "@phosphor-icons/react";
import { Panel, Info } from "./_shared";

// P4 — controlled observed-package-type vocabulary (value → human-readable label).
const OBSERVED_PACKAGE_TYPES = [
  { value: "CARTON_BOX", label: "Carton / Box" },
  { value: "BAG_SACK", label: "Bag / Sack" },
  { value: "CRATE", label: "Crate" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "ROLL_TUBE", label: "Roll / Tube" },
  { value: "OTHER", label: "Other" },
];

// B1 — strip an obsolete mutable "/NN" total from a legacy stored public_label for display only.
const stripTotal = (label) => (label || "").replace(/\/\d+$/, "");

const emptyAddForm = { package_type: "CARTON_BOX", actual_weight_kg: "", length_cm: "", width_cm: "", height_cm: "", fragile: false };

function AddPackageForm({ shipmentId, reload }) {
  const [form, setForm] = useState(emptyAddForm);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    // Durable client-generated idempotency key — one per genuine Add action.
    const key = (window.crypto?.randomUUID?.() || `add-${Date.now()}-${Math.random()}`);
    try {
      await api.post(`/shipments/${shipmentId}/packages/amend/add?idempotency_key=${encodeURIComponent(key)}`, {
        packages: [{
          package_type: form.package_type,
          actual_weight_kg: Number(form.actual_weight_kg),
          length_cm: Number(form.length_cm),
          width_cm: Number(form.width_cm),
          height_cm: Number(form.height_cm),
          fragile: form.fragile,
        }],
      });
      toast.success("Package added. Print its label before Pickup Complete.");
      setForm(emptyAddForm);
      await reload();
    } catch (err) {
      toast.error(err.displayMessage || err?.response?.data?.detail?.message || "Could not add package");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 mt-3 p-3 rounded-lg border border-[hsl(var(--border))]" data-testid="add-package-form">
      <select value={form.package_type} onChange={(e) => set("package_type", e.target.value)} className="input-air12 text-xs" data-testid="add-pkg-type">
        {OBSERVED_PACKAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <input required type="number" step="0.5" min="0.5" placeholder="Wt kg" value={form.actual_weight_kg} onChange={(e) => set("actual_weight_kg", e.target.value)} className="input-air12 text-xs w-20" data-testid="add-pkg-weight" />
      <input required type="number" step="1" min="1" placeholder="L" value={form.length_cm} onChange={(e) => set("length_cm", e.target.value)} className="input-air12 text-xs w-16" data-testid="add-pkg-length" />
      <input required type="number" step="1" min="1" placeholder="W" value={form.width_cm} onChange={(e) => set("width_cm", e.target.value)} className="input-air12 text-xs w-16" data-testid="add-pkg-width" />
      <input required type="number" step="1" min="1" placeholder="H" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="input-air12 text-xs w-16" data-testid="add-pkg-height" />
      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.fragile} onChange={(e) => set("fragile", e.target.checked)} data-testid="add-pkg-fragile" /> Fragile</label>
      <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="add-package-submit">{busy ? "Adding…" : "Missed a package? Add another"}</button>
    </form>
  );
}

export function PackagesPanel({ d, reload, isAdmin, isSender }) {
  const packages = d.packages || [];
  const amend = d.shipment?.current_status === "CANCELLED" ? {} : (d.manifest_amendment || {});
  const activePkgs = packages.filter((p) => p.status !== "REMOVED");
  const labelsReady = activePkgs.length > 0;
  const downloadAllLabels = async (e) => {
    e.preventDefault();
    try {
      const resp = await api.get(`/shipments/${d.shipment.id}/labels.pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, "_blank");
    } catch (err) { toast.error(err.displayMessage || "Could not download labels"); }
  };
  const removePkg = async (p) => {
    if (!window.confirm(`Remove package ${stripTotal(p.public_label)}? Its pickup scan and photo will be cleared.`)) return;
    try {
      await api.post(`/shipments/${d.shipment.id}/packages/${p.id}/remove`, {});
      toast.success(`Package ${stripTotal(p.public_label)} removed`);
      await reload();
    } catch (err) { toast.error(err.displayMessage || err?.response?.data?.detail?.message || "Could not remove package"); }
  };
  const restorePkg = async (p) => {
    try {
      await api.post(`/shipments/${d.shipment.id}/packages/${p.id}/restore`, {});
      toast.success(`Package ${stripTotal(p.public_label)} restored. It needs a fresh pickup scan + photo.`);
      await reload();
    } catch (err) { toast.error(err.displayMessage || err?.response?.data?.detail?.message || "Could not restore package"); }
  };
  return (
    <Panel icon={<QrCode size={16} weight="bold" />} title={`PACKAGES (${activePkgs.length}${packages.length !== activePkgs.length ? ` active / ${packages.length} total` : ""})`}>
      {amend.can_amend && (
        <p className="text-[11px] text-[hsl(var(--muted))] mb-2" data-testid="amend-window-note">
          You can adjust your package list until pickup is completed. Up to {amend.max_active_packages} active packages allowed ({amend.main_package_count} paid + 3).
        </p>
      )}
      {packages.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="no-packages">No packages added yet.</p>
      ) : (
        <>
          <div className="flex justify-end mb-2">
            {labelsReady ? (
              <button type="button" onClick={downloadAllLabels} className="btn-secondary text-xs" data-testid="download-all-labels-button">
                Download all labels ({activePkgs.length})
              </button>
            ) : (
              <span className="text-[11px] text-[hsl(var(--muted))]" data-testid="labels-locked-note">
                Labels become available once shipment documents are generated.
              </span>
            )}
          </div>
          <table className="table-air12">
          <thead>
            <tr>
              <th>Label</th><th>Type</th><th>Actual</th><th>Volumetric</th><th>Dims (cm)</th><th>QR</th><th>Label PDF</th>
              {amend.can_amend && <th>Manage</th>}
            </tr>
          </thead>
          <tbody>
            {packages.map((p) => {
              const removed = p.status === "REMOVED";
              return (
              <tr key={p.id} data-testid={`pkg-row-${stripTotal(p.public_label)}`} className={removed ? "opacity-60" : ""}>
                <td>
                  <span className="id-chip">{stripTotal(p.public_label)}</span>
                  {p.added_after_main && <span className="status-pill status-info text-[10px] ml-1" data-testid={`pkg-added-${stripTotal(p.public_label)}`}>Added</span>}
                  {removed && <span className="status-pill text-[10px] ml-1" data-testid={`pkg-removed-${stripTotal(p.public_label)}`}>Removed</span>}
                </td>
                <td className="mono">{p.package_type_label || p.package_type}</td>
                <td className="mono">{p.actual_weight_kg} kg</td>
                <td className="mono">{p.volumetric_weight_kg} kg</td>
                <td className="mono text-xs">{p.length_cm}×{p.width_cm}×{p.height_cm}</td>
                <td>{!removed && p.qr_code_data_url ? <img src={p.qr_code_data_url} alt="qr" className="w-10 h-10" data-testid={`qr-${stripTotal(p.public_label)}`} /> : "—"}</td>
                <td>
                  {!removed && labelsReady ? (
                    <a href="#" onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const resp = await api.get(`/shipments/${d.shipment.id}/packages/${p.id}/label.pdf`, { responseType: "blob" });
                          window.open(URL.createObjectURL(resp.data), "_blank");
                        } catch (e) { toast.error(e.displayMessage); }
                      }}
                      className="underline text-xs" data-testid={`label-pdf-${stripTotal(p.public_label)}`}
                    >Label PDF</a>
                  ) : <span className="text-xs text-[hsl(var(--muted))]">—</span>}
                </td>
                {amend.can_amend && (
                  <td>
                    {removed ? (
                      <button type="button" onClick={() => restorePkg(p)} className="btn-secondary text-[11px]" data-testid={`restore-pkg-${stripTotal(p.public_label)}`}>Restore</button>
                    ) : (
                      <button type="button" onClick={() => removePkg(p)} disabled={activePkgs.length <= 1} className="btn-secondary text-[11px]" data-testid={`remove-pkg-${stripTotal(p.public_label)}`}>Remove</button>
                    )}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
        {amend.can_amend && (amend.can_add
          ? <AddPackageForm shipmentId={d.shipment.id} reload={reload} />
          : <p className="text-[11px] text-[hsl(var(--muted))] mt-2" data-testid="add-cap-reached">Maximum active packages reached ({amend.max_active_packages}). Remove a package to add another.</p>)}
        </>
      )}
      {isSender && <SenderRiderMeasurement d={d} />}
    </Panel>
  );
}

// P4 — Sender post-pickup dual display: declared vs measured-at-pickup, shown per package only
// when a finalized Rider measurement exists. Airport-verification wording is backend-authoritative.
function SenderRiderMeasurement({ d }) {
  const measured = (d.packages || []).filter((p) => p.rider_measured);
  const wording = d.sender_verification_wording;
  if (measured.length === 0 && !wording) return null;
  return (
    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" data-testid="sender-measurement-section">
      <div className="overline mb-2">PICKUP MEASUREMENT</div>
      {wording && (
        <div className="text-sm mb-3 text-[hsl(var(--muted))]" data-testid="sender-verification-wording">{wording}</div>
      )}
      <div className="space-y-2">
        {measured.map((p) => (
          <div key={p.id} className="grid-card p-3 text-sm" data-testid={`sender-dims-block-${p.public_label}`}>
            <div className="mono text-xs font-semibold mb-2">{p.public_label}</div>
            <div className="grid grid-cols-2 gap-3">
              <Info label="Declared dimensions">
                <span className="mono" data-testid={`sender-dims-declared-${p.public_label}`}>
                  {p.length_cm} × {p.width_cm} × {p.height_cm} cm
                </span>
              </Info>
              <Info label="Measured at pickup">
                <span className="mono" data-testid={`sender-dims-measured-${p.public_label}`}>
                  {p.rider_measured_length_cm} × {p.rider_measured_width_cm} × {p.rider_measured_height_cm} cm
                </span>
              </Info>
              <Info label="Declared package type"><span>{p.package_type_label}</span></Info>
              <Info label="Observed at pickup">
                <span data-testid={`sender-type-measured-${p.public_label}`}>{p.rider_observed_package_type_label}</span>
              </Info>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// AIR12 — Final balance (DELTA) review — AWB-driven balance/status + raise/resend/waiver actions.
// The legacy Captain per-package weight/dimension verification UI is retired (no active workflow;
// Module 9A verify_package route is parked). Underlying verified_* fields remain dormant server-side.

export function VerificationReviewPanel({ d, reload, profile }) {
  const s = d.shipment;
  const isSuperAdmin = profile?.role === "SUPER_ADMIN";
  const [delta, setDelta] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const loadDelta = async () => {
    try {
      const { data } = await api.get(`/checkout/${s.id}/delta`);
      setDelta(data);
    } catch (e) { setDelta(null); }
  };
  useEffect(() => {
    loadDelta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);

  const finalized = !!(delta && delta.finalized);
  const deltaAmount = delta ? Number(delta.delta_amount || 0) : 0;
  const settled = delta ? !!delta.settled : false;
  const cur = delta && delta.current_attempt;
  const raised = !!(delta && delta.raised);
  const waiverAvailable = !!(delta && delta.waiver_available);
  const resendAvailable = !!(delta && delta.resend_available);
  const role = profile?.role;
  // Backend: raise/resend = SA / assigned Source CTOP / Source CTRL; waive = SA / Source CTRL.
  const isSrcCtrl = role === "CTRL" && !!s.source_city_controller_id && s.source_city_controller_id === profile?.id;  // = delta_router._is_assigned_source_ctrl
  const canManageRequest = role === "SUPER_ADMIN" || (role === "CTOP" && s.source_ctop_id === profile?.id) || isSrcCtrl;
  const canWaive = role === "SUPER_ADMIN" || isSrcCtrl;

  const raiseRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/checkout/${s.id}/delta/raise-request`, {});
      toast.success(data.email_sent ? "Payment request raised and emailed to the sender"
                                    : "Payment request raised (email will be retried)");
      await loadDelta(); reload();
    } catch (e) { toast.error(e.displayMessage || "Could not raise payment request"); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/checkout/${s.id}/delta/resend`, {});
      toast.success(data.email_sent ? "Payment request re-sent to the sender"
                                    : "Resend attempted — email delivery failed, please retry");
      await loadDelta();
    } catch (e) { toast.error(e.displayMessage || "Could not resend payment request"); }
    finally { setBusy(false); }
  };

  const waive = async () => {
    if (reason.trim().length < 6) return toast.error("Reason must be at least 6 characters");
    setBusy(true);
    try {
      await api.post(`/checkout/${s.id}/delta/waive`, { reason: reason.trim() });
      toast.success("Balance waived");
      setReason(""); setConfirming(false);
      await loadDelta();
      reload();
    } catch (e) {
      toast.error(e.displayMessage || "Could not waive balance");
    } finally { setBusy(false); }
  };

  return (
    <Panel
      icon={<ShieldCheck size={16} weight="bold" />}
      title="DELTA"
    >
      <div data-testid="verification-review-panel" className="space-y-3">
        {!finalized ? (
          <p className="text-sm text-[hsl(var(--muted))]" data-testid="verification-not-finalized">
            Final balance not available yet.
          </p>
        ) : (
          <>
            {/* Commercial summary */}
            <div className="border border-[hsl(var(--border))] rounded px-3 py-2 text-sm" data-testid="final-verify-commercial">
              {deltaAmount > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[hsl(var(--muted))]">Balance (DELTA) payable</span>
                    <span className="mono font-semibold" data-testid="final-verify-delta-amount">₹{deltaAmount}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[hsl(var(--muted))]">Status</span>
                    <span className="mono" data-testid="final-verify-delta-status">
                      {settled ? (cur && cur.status === "WAIVED" ? "WAIVED" : "PAID") : (cur ? cur.status : "PENDING")}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-[hsl(var(--accent))]" data-testid="final-verify-no-delta">
                  No balance due — final verification did not increase the payable.
                </div>
              )}
            </div>

            {/* Raise Request / Resend (assigned Source CTOP / CTRL / SA). */}
            {deltaAmount > 0 && !settled && canManageRequest && (
              <div className="grid-card p-3 border border-[hsl(var(--border))]" data-testid="delta-request-block">
                <div className="overline mb-1">BALANCE PAYMENT REQUEST</div>
                {!raised ? (
                  <>
                    <p className="text-[11px] text-[hsl(var(--muted))] mb-2" data-testid="delta-request-not-raised">
                      No payment request has been raised. The sender cannot pay until you raise it.
                    </p>
                    <button className="btn-primary text-xs" disabled={busy} onClick={raiseRequest} data-testid="delta-raise-request-button">
                      {busy ? "Raising…" : "Raise payment request"}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-[11px] text-[hsl(var(--accent))] mb-2" data-testid="delta-request-raised">
                      Payment request is active and has been emailed to the sender.
                    </p>
                    <button className="btn-ghost text-xs" disabled={busy || !resendAvailable} onClick={resend} data-testid="delta-resend-button">
                      {busy ? "Resending…" : "Resend payment request"}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Waiver — assigned Source CTRL or SUPER_ADMIN, only before checkout has started. */}
            {deltaAmount > 0 && !settled && (
              canWaive && waiverAvailable ? (
                <div className="grid-card p-3 border border-[hsl(var(--warning)/0.4)]" data-testid="delta-waiver-block">
                  <div className="overline mb-1">WAIVE BALANCE (CTRL / SUPER ADMIN)</div>
                  <label className="text-[11px] text-[hsl(var(--muted))] block">Reason for waiver
                    <input className="input-field text-xs mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delta-waive-reason" />
                  </label>
                  <div className="text-[11px] text-[hsl(var(--muted))] mt-1" data-testid="delta-waive-helper">
                    Enter at least 6 characters. This reason will be retained in the audit history.
                  </div>
                  {!confirming ? (
                    <button className="btn-ghost text-xs mt-2" disabled={busy} onClick={() => setConfirming(true)} data-testid="delta-waive-button">
                      Waive balance payable
                    </button>
                  ) : (
                    <div className="mt-2 flex gap-2" data-testid="delta-waive-confirm">
                      <button className="btn-ghost text-xs" disabled={busy} onClick={() => setConfirming(false)} data-testid="delta-waive-cancel">Cancel</button>
                      <button className="btn-primary text-xs" disabled={busy} onClick={waive} data-testid="delta-waive-confirm-button">
                        {busy ? "Waiving…" : "Confirm waiver"}
                      </button>
                    </div>
                  )}
                </div>
              ) : canWaive && !waiverAvailable ? (
                <div className="text-xs text-[hsl(var(--muted))]" data-testid="delta-waiver-unavailable">
                  Waiver is unavailable — the sender has already started the balance payment.
                </div>
              ) : (
                <div className="text-xs text-[hsl(var(--muted))]" data-testid="delta-waiver-readonly">
                  Source Controller or Super Admin required to waive the balance payable.
                </div>
              )
            )}
          </>
        )}
      </div>
    </Panel>
  );
}


export function ScanCompletenessPanel({ d }) {
  const contexts = ["SOURCE_RECEIPT","DESTINATION_RECEIPT","DELIVERY"];
  const expected = d.packages.length;
  const counts = contexts.map((c) => ({
    ctx: c,
    scanned: d.scans.filter((s) => s.scan_context === c).length,
  }));
  // Read-only scan progress (no override exists).
  return (
    <Panel icon={<QrCode size={16} weight="bold" />} title="SCAN COMPLETENESS">
      <div className="space-y-2 text-sm">
        {counts.map((c) => (
          <div key={c.ctx} className="flex items-center justify-between" data-testid={`scan-${c.ctx}`}>
            <span className="overline text-[10px]">{c.ctx.replace(/_/g, " ")}</span>
            <span className={`mono text-sm font-semibold ${c.scanned >= expected && expected > 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--muted))]"}`}
                  data-testid={`scan-count-${c.ctx}`}>
              {c.scanned}/{expected}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// P4 — Source CTOP / Super Admin Rider-measurement panel (operational read-only + correction).
const RIDER_CORRECTION_ERRORS = {
  CORRECTION_REASON_REQUIRED: "Enter a reason for the correction.",
  CAPTAIN_VERIFICATION_STARTED: "Airport verification has begun, so the correction window is now closed.",
  RIDER_MEASUREMENT_NOT_FOUND: "There is no finalized pickup measurement to correct.",
  PICKUP_NOT_COMPLETED: "Pickup has not been completed yet.",
  PICKUP_STAGE_INVALID: "This action is not available at the current shipment stage.",
  INVALID_DIMENSIONS: "Enter valid positive dimensions within the supported range.",
  INVALID_PACKAGE_TYPE: "Select a valid observed package type.",
  PACKAGE_NOT_IN_SHIPMENT: "This package is not part of the shipment.",
};

function riderCorrectionError(e) {
  const detail = e?.response?.data?.detail;
  const code = detail?.code;
  if (code && RIDER_CORRECTION_ERRORS[code]) return RIDER_CORRECTION_ERRORS[code];
  if (e?.response?.status === 403) return "Only the assigned Source CTOP or a Super Admin can correct this measurement.";
  return detail?.message || e?.displayMessage || "Could not correct the measurement.";
}

export function RiderMeasurementPanel({ d, reload, isSuperAdmin, isSourceCtop, correctionWindowOpen }) {
  const pkgs = d.packages || [];
  const canCorrect = !!(correctionWindowOpen && (isSuperAdmin || isSourceCtop));
  return (
    <Panel icon={<Ruler size={16} weight="bold" />} title="RIDER PICKUP MEASUREMENT">
      <div data-testid="rider-measurement-panel" className="space-y-2">
        <p className="text-[11px] text-[hsl(var(--muted))]">
          Observed package dimensions recorded by the pickup rider. This is separate from airport verification.
        </p>
        {pkgs.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted))]" data-testid="rider-measurement-none">No packages.</p>
        ) : (
          pkgs.map((p) => (
            <RiderMeasurementRow
              key={p.id}
              pkg={p}
              shipmentId={d.shipment.id}
              reload={reload}
              canCorrect={canCorrect}
            />
          ))
        )}
      </div>
    </Panel>
  );
}

function RiderMeasurementRow({ pkg, shipmentId, reload, canCorrect }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [type, setType] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const hasRider = pkg.rider_measured_length_cm != null;
  const state = pkg.rider_measurement_state || (pkg.rider_measured ? "Recorded" : "Not measured");
  const correctable = canCorrect && hasRider && pkg.rider_measurement_locked;

  const openForm = () => {
    setLength(pkg.rider_measured_length_cm != null ? String(pkg.rider_measured_length_cm) : "");
    setWidth(pkg.rider_measured_width_cm != null ? String(pkg.rider_measured_width_cm) : "");
    setHeight(pkg.rider_measured_height_cm != null ? String(pkg.rider_measured_height_cm) : "");
    setType(pkg.rider_observed_package_type || "");
    setReason("");
    setConfirming(false);
    setOpen(true);
  };

  const canSubmit =
    parseFloat(length) > 0 && parseFloat(width) > 0 && parseFloat(height) > 0 && !!type && reason.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${shipmentId}/packages/${pkg.id}/rider-measurement/correct`, {
        length_cm: parseFloat(length),
        width_cm: parseFloat(width),
        height_cm: parseFloat(height),
        observed_package_type: type,
        reason: reason.trim(),
      });
      toast.success(`Measurement corrected for ${pkg.public_label}`);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(riderCorrectionError(e));
      const code = e?.response?.data?.detail?.code;
      if (code === "CAPTAIN_VERIFICATION_STARTED" || code === "PICKUP_STAGE_INVALID" || code === "RIDER_MEASUREMENT_NOT_FOUND") {
        setOpen(false);
        reload();
      }
    } finally {
      setBusy(false);
    }
  };

  const toLabel = (v) => (OBSERVED_PACKAGE_TYPES.find((o) => o.value === v) || {}).label || v;

  return (
    <div className="border border-[hsl(var(--border))] rounded" data-testid={`rider-correct-row-${pkg.public_label}`}>
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="id-chip text-xs">{pkg.public_label}</span>
          <span className="status-pill" data-testid={`rider-measure-state-${pkg.public_label}`}>{state}</span>
        </div>
        {correctable && (
          <button
            className="text-xs underline text-[hsl(var(--muted))]"
            onClick={() => (open ? setOpen(false) : openForm())}
            data-testid={`rider-correct-toggle-${pkg.public_label}`}
          >
            {open ? "Cancel" : "Correct"}
          </button>
        )}
      </div>
      <div className="px-3 pb-2 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="overline">Declared</div>
          <div className="mono">{pkg.length_cm}×{pkg.width_cm}×{pkg.height_cm} · {pkg.package_type_label}</div>
        </div>
        <div>
          <div className="overline">Measured at pickup</div>
          <div className="mono" data-testid={`rider-measure-values-${pkg.public_label}`}>
            {hasRider
              ? `${pkg.rider_measured_length_cm}×${pkg.rider_measured_width_cm}×${pkg.rider_measured_height_cm} · ${pkg.rider_observed_package_type_label}`
              : "—"}
          </div>
        </div>
      </div>
      {pkg.rider_measurement_correction_reason && (
        <div className="mx-3 mb-2 text-[11px] text-[hsl(var(--muted))]" data-testid={`rider-correct-meta-${pkg.public_label}`}>
          Corrected{pkg.rider_measurement_corrected_at ? ` · ${fmtDT(pkg.rider_measurement_corrected_at)}` : ""} — {pkg.rider_measurement_correction_reason}
        </div>
      )}
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-[hsl(var(--border))] pt-3" data-testid={`rider-correct-form-${pkg.public_label}`}>
          <div className="grid grid-cols-3 gap-2">
            <input className="input-field text-xs" type="number" min="0" step="any" placeholder="L (cm)" value={length} onChange={(e) => setLength(e.target.value)} data-testid={`rider-correct-length-${pkg.public_label}`} />
            <input className="input-field text-xs" type="number" min="0" step="any" placeholder="W (cm)" value={width} onChange={(e) => setWidth(e.target.value)} data-testid={`rider-correct-width-${pkg.public_label}`} />
            <input className="input-field text-xs" type="number" min="0" step="any" placeholder="H (cm)" value={height} onChange={(e) => setHeight(e.target.value)} data-testid={`rider-correct-height-${pkg.public_label}`} />
          </div>
          <select className="input-field text-xs" value={type} onChange={(e) => setType(e.target.value)} data-testid={`rider-correct-type-${pkg.public_label}`}>
            <option value="">Select observed package type</option>
            {OBSERVED_PACKAGE_TYPES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
          <input className="input-field text-xs" placeholder="Reason for correction" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`rider-correct-reason-${pkg.public_label}`} />
          {!confirming ? (
            <button className="btn-primary text-xs" disabled={!canSubmit} onClick={() => setConfirming(true)} data-testid={`rider-correct-review-${pkg.public_label}`}>
              Review correction
            </button>
          ) : (
            <div className="space-y-2 border border-[hsl(var(--border))] rounded p-2" data-testid={`rider-correct-confirm-box-${pkg.public_label}`}>
              <div className="text-[11px]">
                <div className="overline">CONFIRM CHANGE</div>
                <div className="mono">From: {hasRider ? `${pkg.rider_measured_length_cm}×${pkg.rider_measured_width_cm}×${pkg.rider_measured_height_cm} · ${pkg.rider_observed_package_type_label}` : "—"}</div>
                <div className="mono">To: {length}×{width}×{height} · {toLabel(type)}</div>
                <div>Reason: {reason}</div>
              </div>
              <div className="flex gap-2">
                <button className="btn-primary text-xs" disabled={busy || !canSubmit} onClick={submit} data-testid={`rider-correct-confirm-${pkg.public_label}`}>
                  {busy ? "Saving…" : "Confirm correction"}
                </button>
                <button className="btn-ghost text-xs" disabled={busy} onClick={() => setConfirming(false)} data-testid={`rider-correct-back-${pkg.public_label}`}>
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Module 7.2E4-R — friendly audit-event labels (display only; event structure unchanged).
